import { getRoot } from 'mobx-state-tree';
import { clone } from 'lodash-es';
import { getSignedAssetUrl } from './S3Client';
import schema, { generateType } from './Schema';
export const MimeTypeMap = {
    image: 'image/*',
    video: 'video/*',
    audio: 'audio/*',
    pdf: 'application/pdf',
    zip: 'application/zip',
    download: '*',
    any: '*',
};
/** Fallback when browsers omit MIME or use octet-stream (common for drag-and-drop / some MP3s). */
export const AUDIO_FILE_EXT = /\.(mp3|m4a|aac|wav|flac|ogg|opus|wma)$/i;
const VIDEO_FILE_EXT = /\.(mp4|mpe?g|mov|webm|m4v|avi|mkv)$/i;
const IMAGE_FILE_EXT = /\.(jpe?g|png|gif|webp|svg|bmp|ico)$/i;
const SPREADSHEET_FILE_EXT = /\.(csv|tsv|xlsx?|xlsm|ods)$/i;
export function inferAssetTypeFromFileName(fileName) {
    if (!fileName?.trim()) {
        return null;
    }
    if (AUDIO_FILE_EXT.test(fileName))
        return 'audio';
    if (VIDEO_FILE_EXT.test(fileName))
        return 'video';
    if (IMAGE_FILE_EXT.test(fileName))
        return 'image';
    if (/\.pdf$/i.test(fileName))
        return 'pdf';
    if (/\.zip$/i.test(fileName))
        return 'zip';
    if (SPREADSHEET_FILE_EXT.test(fileName))
        return 'application';
    return null;
}
export const getAssetTypeFromMimeType = (mimeType) => {
    const mime = (mimeType ?? '').trim();
    if (!mime) {
        return 'any';
    }
    if (mime === 'application/pdf') {
        return 'pdf';
    }
    if ([
        'application/zip',
        'application/x-zip',
        'application/x-zip-compressed',
        'multipart/x-zip'
    ].includes(mime)) {
        return 'zip';
    }
    return mime.split('/')[0];
};
/**
 * Best-effort asset type when creating an Asset from a browser File upload.
 */
export function getAssetTypeFromFile(file) {
    const mime = (file?.type ?? '').trim();
    const extType = inferAssetTypeFromFileName(file?.name ?? '');
    if (!mime || mime === 'application/octet-stream') {
        if (SPREADSHEET_FILE_EXT.test(file?.name ?? '')) {
            return 'application';
        }
        return extType ?? 'any';
    }
    const fromMime = getAssetTypeFromMimeType(mime);
    if (mime.startsWith('audio/')
        || mime.startsWith('video/')
        || mime.startsWith('image/')) {
        return fromMime;
    }
    if (fromMime === 'application' && extType) {
        return extType;
    }
    return fromMime ?? extType ?? 'any';
}
export const CACHE_DURATION = 10 * 60 * 1000; // ten minutes
// export const CACHE_DURATION = 30 * 1000; // 30 seconds
// eslint-disable-next-line @typescript-eslint/default-param-last
export const getPublicUrl = async (asset, size = 'original', store) => {
    const repository = await store.load('repository', asset.repository.key);
    return `https://${repository?.bucket}.s3.us-east-1.amazonaws.com/${asset.path}/${size}`;
};
export const Asset = generateType(schema.getModel('asset'))
    .views(() => ({
    isAsset() {
        return true;
    },
    isCourse() {
        return false;
    },
    isDocument() {
        return false;
    },
    isProgram() {
        return false;
    },
    isMeeting() {
        return false;
    },
    isMicrocast() {
        return false;
    },
}))
    .actions((self) => ({
    // This is an auto-generated example action.
    log() {
        console.log(JSON.stringify(self));
    },
    setSignedUrlMap(newMap) {
        self.signedUrlMap = newMap;
    },
    /**
     * Returns a url that can be used up until expiry time
     * @param size
     */
    async getSignedUrl(size) {
        const mappedSize = size === 'videoThumb' ? 'keyframes/frame_005.jpeg' : size;
        // check if we have it
        let entry = self.signedUrlMap[size];
        if (!entry || Date.now() > entry.expiry) {
            const rootModel = getRoot(self);
            // sign it and put it in the map
            if (self.access === 'public') {
                // get the url
                const publicUrl = await getPublicUrl(self, mappedSize, rootModel);
                entry = { url: publicUrl, expiry: Number.MAX_SAFE_INTEGER };
            }
            else {
                // sign it and store that
                entry = {
                    url: await getSignedAssetUrl(self, mappedSize, rootModel),
                    expiry: Date.now() + CACHE_DURATION,
                };
            }
            const newMap = clone(self.signedUrlMap);
            newMap[size] = entry;
            self.setSignedUrlMap(newMap);
        }
        return entry;
    },
}))
    .views((self) => ({
    get thumb200h() {
        return `${self.path}/thumb200h`;
    },
    get thumb200w() {
        return `${self.path}/thumb200w`;
    },
    get thumb() {
        if (self.type === 'application' || self.type === 'pdf') {
            return `${self.path}/preview.png`;
        }
        const filePath = self.type === 'video' ? '/keyframes/frame_005.jpeg' : '/thumb400w';
        return self.path + filePath;
    },
    get original() {
        return `${self.path}/original`;
    },
    /*
     * returns the url if its in cache, null otherwise
     */
    getPrecachedUrl(size) {
        const entry = self.signedUrlMap[size];
        if (entry) {
            // console.log('PrecachedURL', entry.url);
            return entry.url;
        }
        // console.log(`No precachedUrl for ${self.id}:${size}`);
        return undefined;
    },
    itemImage() {
        if (self.type === 'video') {
            return self.thumb;
        }
        if (self.type === 'image') {
            return self;
        }
        return undefined;
    },
}));
export const syncDriveFilesGQL = ( /* GraphQL */`
  mutation SyncDriveFiles(
    $folderIds: [String]!,
    $organizationID: String!,
    $repositoryID: String!,
    $libraryID: String!
  ) {
    syncDriveFiles(
      folderIds: $folderIds,
      organizationID: $organizationID,
      repositoryID: $repositoryID,
      libraryID: $libraryID
    ) {
      statusCode
      message
      results {
        id
        name
        type
        mimeType
        key
      }
    }
  }
`);
export const processAssetGQL = (isPortalApi) => ( /* GraphQL */`
  mutation ProcessAsset(
    ${!isPortalApi ? '$organizationID: String!' : ''}
    $assetID: String!
  ) {
    processAsset(
      ${!isPortalApi ? 'organizationID: $organizationID' : ''}
      assetID: $assetID
    ) {
      ${schema.getGqlPropertyList('asset', isPortalApi, false, false).join(' ')}
    }
  }
`);
//# sourceMappingURL=AssetModel.js.map